import React, { useEffect, useState } from 'react';
import api, { buildWhatsAppLink } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
  confirmed: 'bg-green-500/20 text-green-300 border-green-500/40',
  cancelled: 'bg-red-500/20 text-red-300 border-red-500/40',
  completed: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
};

const AdminReservations = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get('/reservations').then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/reservations/${id}/status`, { status });
      toast.success('Estado actualizado');
      load();
    } catch { toast.error('Error'); }
  };
  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta reserva?')) return;
    try {
      await api.delete(`/reservations/${id}`);
      toast.success('Reserva eliminada');
      load();
    } catch { toast.error('Error'); }
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black text-white">Reservas</h1>
          <p className="text-white/60">Gestiona todas las reservas de clientes</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0f0f0f] border-white/10">
            <SelectItem value="all" className="text-white">Todas</SelectItem>
            <SelectItem value="pending" className="text-white">Pendientes</SelectItem>
            <SelectItem value="confirmed" className="text-white">Confirmadas</SelectItem>
            <SelectItem value="completed" className="text-white">Completadas</SelectItem>
            <SelectItem value="cancelled" className="text-white">Canceladas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/60 text-white/70">
              <tr>
                <th className="text-left p-4">Cliente</th>
                <th className="text-left p-4">Tipo</th>
                <th className="text-left p-4">Detalles</th>
                <th className="text-left p-4">Fecha</th>
                <th className="text-left p-4">Pax</th>
                <th className="text-left p-4">Estado</th>
                <th className="text-left p-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-white/40">No hay reservas</td></tr>
              )}
              {filtered.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="p-4">
                      <div className="font-semibold text-white">{r.full_name}</div>
                      <div className="text-white/50 text-xs">{r.email}</div>
                    </td>
                    <td className="p-4"><span className="text-white/80 capitalize">{r.type}</span></td>
                    <td className="p-4 text-white/70 max-w-xs truncate">{r.type === 'excursion' ? r.excursion_title : `${r.pickup} → ${r.destination}`}</td>
                    <td className="p-4 text-white/70">{r.date} {r.time}</td>
                    <td className="p-4 text-white/70">{r.passengers}</td>
                    <td className="p-4">
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger className={`h-8 px-3 text-xs border ${statusColors[r.status] || ''}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0f0f0f] border-white/10">
                          <SelectItem value="pending" className="text-white">Pendiente</SelectItem>
                          <SelectItem value="confirmed" className="text-white">Confirmada</SelectItem>
                          <SelectItem value="completed" className="text-white">Completada</SelectItem>
                          <SelectItem value="cancelled" className="text-white">Cancelada</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <a href={buildWhatsAppLink(`Hola ${r.full_name}, sobre tu reserva...`)} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded bg-green-600/20 text-green-400 hover:bg-green-600/40 flex items-center justify-center"><MessageCircle className="w-4 h-4" /></a>
                        <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="w-8 h-8 rounded bg-white/5 text-white/70 hover:bg-white/10 flex items-center justify-center">
                          {expanded === r.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <button onClick={() => remove(r.id)} className="w-8 h-8 rounded bg-red-600/20 text-red-400 hover:bg-red-600/40 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="bg-black/40">
                      <td colSpan={7} className="p-5 text-sm text-white/70">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div><strong className="text-white">WhatsApp:</strong> {r.whatsapp}</div>
                          <div><strong className="text-white">Tipo viaje:</strong> {r.trip_type}</div>
                          <div><strong className="text-white">Vehículo:</strong> {r.vehicle || '-'}</div>
                          <div><strong className="text-white">Creada:</strong> {new Date(r.created_at).toLocaleString()}</div>
                          {r.comments && <div className="md:col-span-2"><strong className="text-white">Comentarios:</strong> {r.comments}</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReservations;
